/**
 * NIP-25 Emoji Reactions Service
 * 
 * Enhanced reaction system with emoji support
 */

import { Event } from 'nostr-tools'
import { getNostrPool } from '../lib/nostrPool'
import { DEFAULT_RELAYS } from '../config/constants'
import { reactToEvent, deleteEvent } from '../lib/nostr'

// Common emoji reactions
export const REACTION_EMOJIS = ['👍', '❤️', '🔥', '😂', '😢', '🤔', '👎'] as const
export type ReactionEmoji = typeof REACTION_EMOJIS[number] | '+' | '-'

interface ReactionCount {
  emoji: string
  count: number
  userReacted: boolean
  reactionId?: string
}

interface EventReactions {
  [emoji: string]: ReactionCount
}

/**
 * Fetch all reactions for an event
 */
export async function fetchEventReactions(
  eventId: string,
  userPubkey?: string
): Promise<EventReactions> {
  const pool = getNostrPool()
  
  return new Promise((resolve) => {
    const reactions: EventReactions = {}
    
    const filter = {
      kinds: [7],
      '#e': [eventId],
      limit: 200
    }
    
    const sub = pool.subscribeMany(DEFAULT_RELAYS, filter, {
      onevent(event: Event) {
        const emoji = event.content || '+'
        
        if (!reactions[emoji]) {
          reactions[emoji] = {
            emoji,
            count: 0,
            userReacted: false
          }
        }
        
        reactions[emoji].count++
        
        if (userPubkey && event.pubkey === userPubkey) {
          reactions[emoji].userReacted = true
          reactions[emoji].reactionId = event.id
        }
      },
      oneose() {
        resolve(reactions)
        sub.close()
      }
    })

    setTimeout(() => {
      sub.close()
      resolve(reactions)
    }, 5000)
  })
}

/**
 * Add emoji reaction to an event
 */
export async function addReaction(
  eventId: string,
  authorPubkey: string,
  emoji: string,
  sk?: Uint8Array | null,
  topic?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!sk && !window.nostr) {
      return { success: false, error: 'Giriş yapmalısınız' }
    }

    await reactToEvent(eventId, authorPubkey, emoji, sk, topic)
    return { success: true }

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Reaksiyon eklenemedi'
    }
  }
}

/**
 * Remove a reaction
 */
export async function removeReaction(
  reactionId: string,
  sk?: Uint8Array | null,
  topic?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!sk && !window.nostr) {
      return { success: false, error: 'Giriş yapmalısınız' }
    }

    await deleteEvent(reactionId, 'Reaksiyon geri alındı', sk, topic)
    return { success: true }

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Reaksiyon kaldırılamadı'
    }
  }
}

/**
 * Toggle emoji reaction
 */
export async function toggleReaction(
  eventId: string,
  authorPubkey: string,
  emoji: string,
  existingReactionId?: string,
  sk?: Uint8Array | null,
  topic?: string
): Promise<{ success: boolean; isReacted: boolean; error?: string }> {
  if (existingReactionId) {
    // Remove existing reaction
    const result = await removeReaction(existingReactionId, sk, topic)
    return { ...result, isReacted: false }
  } else {
    // Add new reaction
    const result = await addReaction(eventId, authorPubkey, emoji, sk, topic)
    return { ...result, isReacted: true }
  }
}

/**
 * Get reaction summary for display
 */
export function getReactionSummary(reactions: EventReactions): {
  topReactions: ReactionCount[]
  totalCount: number
} {
  const allReactions = Object.values(reactions)
  const totalCount = allReactions.reduce((sum, r) => sum + r.count, 0)
  
  // Sort by count and get top 5
  const topReactions = allReactions
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
  
  return { topReactions, totalCount }
}
